import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
  amplifyCredentialProviderActions,
  amplifyServiceActions,
  backendIotDataApiActions,
  cognitoIdentityApiActions,
  stsApiActions
} from '@core/iot-data/iot-data.actions';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';
import { v4 } from 'uuid';

export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  expiration?: number;
}

export interface CallerIdentity {
  account: string | undefined;
  arn: string | undefined;
  userId: string | undefined;
}

interface IotConnectionInfo {
  identityPoolId: string;
  iotDataEndpoint: string;
  providerName: string;
}

export interface IotDataState {
  callerIdentity: CallerIdentity | undefined;
  connectionState: string | undefined;
  credentials: AwsCredentials;
  iotConnectionInfo: IotConnectionInfo | undefined;
  identityId: string | undefined;
  idToken: string | undefined;
}

const initialState: IotDataState = {
  callerIdentity: undefined,
  connectionState: undefined,
  credentials: undefined,
  iotConnectionInfo: undefined,
  identityId: undefined,
  idToken: undefined,
};

export const iotDataFeature = createFeature({
  name: 'IoT-Data',
  reducer: createReducer(initialState,
    on(amplifyCredentialProviderActions.userOrTenantChanged, (): IotDataState => initialState),
    on(amplifyCredentialProviderActions.clearCredentialsAndIdentityId, (state): IotDataState => ({
      ...state,
      identityId: undefined,
      credentials: undefined,
    })),
    on(amplifyServiceActions.pubSubConnectionStateUpdate, (state, { connectionState }): IotDataState => ({
      ...state,
      connectionState,
    })),
    on(backendIotDataApiActions.getIotConnectionInfoSuccess, (state, { iotConnectionInfo }): IotDataState => ({
      ...state,
      iotConnectionInfo: {
        identityPoolId: iotConnectionInfo.identityPoolId,
        iotDataEndpoint: iotConnectionInfo.iotDataEndpoint,
        providerName: iotConnectionInfo.providerName,
      },
      idToken: iotConnectionInfo.idToken,
    })),
    on(backendIotDataApiActions.getFederationTokenSuccess, (state, { idToken }): IotDataState => ({
      ...state,
      idToken,
    })),
    on(cognitoIdentityApiActions.getIdSuccess, (state, { identityId }): IotDataState => ({
      ...state,
      identityId,
    })),
    on(stsApiActions.getCallerIdentitySuccess, (state, { callerIdentity }): IotDataState => ({
      ...state,
      callerIdentity,
    })),
    on(cognitoIdentityApiActions.getCredentialsForIdentitySuccess, (state, { response}): IotDataState => ({
      ...state,
      credentials: {
        accessKeyId: response.Credentials.AccessKeyId,
        secretAccessKey: response.Credentials.SecretKey,
        sessionToken: response.Credentials.SessionToken,
        expiration: response.Credentials.Expiration,
      },
    })),
  ),
  extraSelectors: ({
    selectCredentials,
    selectIotConnectionInfo,
    selectIdentityId,
    selectIdToken,
  }) => {
    return {
      selectGetIdRequest: createSelector(
        selectIotConnectionInfo,
        selectIdToken,
        (iotConnectionInfo, idToken) => {
          if (!iotConnectionInfo || !idToken)
            return null;

          return {
            IdentityPoolId: iotConnectionInfo.identityPoolId,
            Logins: {
              [iotConnectionInfo.providerName]: idToken,
            },
          };
        },
      ),
      selectGetCredentialsForIdentityRequest: createSelector(
        selectIotConnectionInfo,
        selectIdentityId,
        selectIdToken,
        (iotConnectionInfo, identityId, idToken) => {
          if (!iotConnectionInfo || !identityId || !idToken)
            return null;

          return {
            IdentityId: identityId,
            Logins: {
              [iotConnectionInfo.providerName]: idToken,
            },
          };
        },
      ),
      selectAwsCredentials: createSelector(
        selectIdentityId,
        selectCredentials,
        (identityId, credentials) => {
          if (!identityId || !credentials || !credentials.accessKeyId)
            return null;

          return {
            identityId,
            credentials: {
              ...credentials,
              expiration: credentials.expiration == null ? null : new Date(credentials.expiration * 1000),
            },
          };
        },
      ),
      selectIotDataRegion: createSelector(
        selectIotConnectionInfo,
        iotConnectionInfo => iotConnectionInfo?.iotDataEndpoint?.split('.')[2] ?? null,
      ),
      selectIotDataWsOverMqttEndpoint: createSelector(
        selectIotConnectionInfo,
        iotConnectionInfo => iotConnectionInfo?.iotDataEndpoint == null ? null : `wss://${iotConnectionInfo.iotDataEndpoint}/mqtt`,
      ),
    };
  }
});

export const selectPubSubOptions = createSelector(
  userTenantFeature.selectSelectedTenantId,
  iotDataFeature.selectIotDataRegion,
  iotDataFeature.selectIotDataWsOverMqttEndpoint,
  (tenantId, region, wsOverMqttEndpoint) => {
    if (!tenantId || !region || !wsOverMqttEndpoint)
      return null;

    return {
      clientId: `cco-frontend-client-${ tenantId }-${ v4() }`,
      region,
      endpoint: wsOverMqttEndpoint,
    };
  }
);
